import { FileInfo } from './file.model';
import { ThirdpartyRight } from './thirdparty-right.model';

/** Zipped reference, produced client-side from the parallel arrays below. */
export type PriceReference = {
  item_id: number | null;
  item_uid: string | null;
  value: number | null;
};

/**
 * One section of the German, filled-in formula breakdown stored by the
 * rg-calculated price source under details.extra.breakdown.
 */
export type RgPriceBreakdownSection = {
  titel: string;
  formel: string;
  berechnung: string;
  werte: Record<string, string>;
};

export type PriceDetails = {
  origin: string;
  method: string;
  aggregation: string;
  match_criteria: string[];
  candidate_count: number;
  /** Parallel arrays — index-aligned (item id, uid, value). */
  reference_item_ids: number[];
  reference_item_uids: string[];
  reference_values: number[];
  determined_at: string;
  /** Source/strategy specifics (e.g. the GPT source's researched source links). */
  extra?: {
    links?: string[];
    citations?: { url: string; title: string | null }[];
    web_search_sources?: string[];
    /** Deduped display links, each flagged whether it is an authoritative source. */
    source_links?: { url: string; verified: boolean }[];
    [key: string]: unknown;
  };
  /** Matched values are stored as top-level keys named by match_criteria. */
  [key: string]: unknown;
};

/**
 * Parameters submitted to the "rg-calculated" price endpoint. The five factors
 * are pre-computed in the frontend (e.g. Zustand-Note → factor, Zubehör dropdown
 * → factor) and sent as plain numbers. Dates are ISO date strings (yyyy-MM-dd).
 */
export type RgCalculatedPricePayload = {
  acquisition_price: number;
  valuation_date: string;
  acquisition_date: string;
  residual_end_percent: number;
  useful_life_years: number;
  condition_factor: number;
  accessories_factor: number;
  repairs_factor: number;
  overhaul_factor: number;
  work_quality_factor: number;
};

/**
 * What a re-run of the AI autofill would use for this item, read before the
 * dialog opens.
 *
 * `scope` decides which prompt block the analysis runs with. It comes off
 * `inventory_items.autofill_scope`, written by the import from the archive and
 * by each replay from the scope it used. Items captured before that column
 * existed carry none, so `null` stays a normal answer and the dialog has to
 * offer the choice rather than hide it.
 */
export type AutofillReplayInfo = {
  inventory_item_id: number;
  scope: string | null;
  available_scopes: string[];
  has_images: boolean;
};

export type InventoryItemPriceHistory = {
  id: number;
  inventory_item_id: number;
  price_origin: string;
  price: number | string | null;
  price_is_netto: boolean;
  price_crawled_at: string | null;
  details: PriceDetails | null;
  created_at: string | null;
  updated_at: string | null;
};

export type InventoryItem = {
  $objectID?: string;
  id?: number;
  uid: string;
  project_location_id: number | null;
  creator_id: number | null;
  full_view_file: FileInfo | null;
  full_view_file_id: number | null;
  files: FileInfo[];
  item_number: number | null;
  title: string | null;
  building: string | null;
  room: string | null;
  storage_location: string | null;
  brand: string | null;
  description: string | null;
  serial_number: string | null;
  quantity: number;
  unit_of_quantity: string;
  internal_remark: string | null;
  tangible_asset_type: string;
  type: string | null;
  goods_type: string;
  condition: number | null;
  color: string | null;
  structure: string | null;
  dimension_length?: number | null;
  dimension_width: number | null;
  dimension_height: number | null;
  dimension_depth: number | null;
  dimension_diameter: number | null;
  weight: number | null;
  liquidation_value: number | null;
  continuation_value: number | undefined | null;
  disposal_costs: number | null;
  // Frontend-only: the ratio of the two amounts, or the asset type's preset.
  // Never stored, never transmitted — see `calculateValueFactorByAssetType`.
  value_factor?: number | null;
  // travel_path: number; depredated!
  work_zone: string | null;
  operating_hours: number | null;
  construction_year: number | null;
  drive: string | null;
  general_inspection: string | null;
  first_registration_day: number | null;
  first_registration_month: number | null;
  first_registration_year: number | null;
  mileage: number | null;
  vin: string | null;
  license_plate: string | null;
  features: string | null;
  marked_for_check: boolean;
  inventory_item_thirdparty_rights: ThirdpartyRight[];
  for_auction: boolean;
};

export type InventoryItemWithoutThirdpartyRights = Omit<
  InventoryItem,
  'inventory_item_thirdparty_rights'
>;

export type LabelKey = {
  key: string;
  label: string;
};

export type ScanDocument = {
  title: string;
  files: string[];
};

export type AppInventoryItem = Partial<InventoryItem> & {
  project_id: number;
  user_token: string;
  user_access_code?: string;
  user_id: number;
  documents?: ScanDocument[];
  complete_views?: string[];
  detail_views?: string[];
  identification?: string[];
  thirdparty_right_objectId?: string;
  api_ai_hash?: string;
  start_editing_timestamps: number[];
  stop_editing_timestamps: number[];
  detail_videos?: string[];
  ai_detail_views?: string[];
  mark_synced: boolean;
  // Content scope of the item (RIEG-249 WP4). One of the `inventory_scopes`
  // keys; defaults to 'noContent'. Mobile-only — the base InventoryItem (used
  // by fe-web) is unchanged. Required with a create-time default; pre-feature
  // CouchDB items lack it and are tolerated via the runtime scope default.
  scope: string;
  // How the item was captured (RIEG-249 WP4/WP5): 'mitKI' | 'ohneKI' |
  // 'schnellmodus'. Defaults to 'mitKI'. Distinct from the auto-advance
  // `fastMode` UI signal (KTD2). Required with a create-time default;
  // pre-feature items are tolerated via the captureBehaviorFor mitKI fallback.
  capture_mode: string;
};

export const defaultInventoryItem: InventoryItem = {
  id: undefined,
  uid: '',
  project_location_id: null,
  creator_id: null,
  full_view_file: null,
  full_view_file_id: null,
  files: [],
  item_number: null,
  title: null,
  building: null,
  room: null,
  storage_location: null,
  brand: null,
  description: null,
  serial_number: null,
  quantity: 1,
  unit_of_quantity: 'Stück',
  internal_remark: null,
  tangible_asset_type: 'operatingAndBusinessEquipment',
  type: null,
  goods_type: 'freeMass',
  condition: null,
  color: null,
  structure: null,
  dimension_length: null,
  dimension_width: null,
  dimension_height: null,
  dimension_depth: null,
  dimension_diameter: null,
  weight: null,
  liquidation_value: null,
  continuation_value: null,
  disposal_costs: null,
  work_zone: null,
  operating_hours: null,
  construction_year: null,
  drive: null,
  first_registration_day: null,
  first_registration_month: null,
  first_registration_year: null,
  mileage: null,
  vin: null,
  license_plate: null,
  general_inspection: null,
  marked_for_check: false,
  features: null,
  inventory_item_thirdparty_rights: [],
  for_auction: true,
};

// Every call site already hands these in possibly-null — the grid reads raw
// cell values, both detail forms read a partially filled item. The type says so
// now instead of pretending otherwise.
export type InventoryItemValues = {
  tangible_asset_type: string | null | undefined;
  value_factor: number | null | undefined;
  liquidation_value: number | null | undefined;
  continuation_value: number | null | undefined;
};

export type ResolvedInventoryItemValues = {
  value_factor: number | null | undefined;
  liquidation_value: number | null | undefined;
  continuation_value: number | null | undefined;
};

// One selected dataset as the bulk builder needs it: its stored values plus the
// id the patch is addressed to. `value_factor` is optional — a caller that
// already materialised it (fe-web's grid does) is trusted, one that did not
// gets it resolved from the stored amounts and the asset type.
export type InventoryBulkDataset = Partial<InventoryItem> & { id: number };

export type InventoryBulkPatch = Partial<InventoryItem> & { id: number };

export type InventoryBulkPatchPlan = {
  patches: InventoryBulkPatch[];
  // KTD27: patches and nothing else. The datasets where a money entry resolved
  // to nothing are listed nowhere: the confirmation no longer breaks the
  // selection down by money (R50), so a second list beside the patches would be
  // an unread source of truth — and the category stays observable in the output
  // anyway, as a patch carrying no money field, or as no patch at all when
  // money was the only thing entered.
};

export const calculateValueFactorByAssetType = (
  tangible_asset_type: string | null | undefined,
  liquidation_value: number | null | undefined,
  continuation_value: number | null | undefined
) =>
  liquidation_value && continuation_value
    ? continuation_value / liquidation_value
    : tangible_asset_type === 'operatingAndBusinessEquipment'
    ? 1.5
    : tangible_asset_type === 'vehicle'
    ? 1.28
    : tangible_asset_type === 'machine'
    ? 1.5
    : 1;

/**
 * The three fields the factor interlocks, and the one place they are named.
 *
 * `tangible_asset_type` is entered like any other column and never moves an
 * amount by itself, so it is not one of them. Every surface that has to know
 * which fields relate to each other — the builder here, the grid's single-row
 * derive, the bulk form's money-floor and entered-amount checks — imports this
 * rather than repeating it, so a fourth such field would be added once.
 *
 * It is not the bulk form's read-only lock. That lock runs between the
 * Fortführungswert and the Faktor alone (R42), so it names those two itself and
 * deliberately never closes the Liquidationswert.
 */
export const INTERLOCKED_VALUE_FIELDS = [
  'liquidation_value',
  'continuation_value',
  'value_factor',
] as const;

export type InterlockedValueField = (typeof INTERLOCKED_VALUE_FIELDS)[number];

// The largest amount a `decimal(8,2)` money column can hold. A derived amount
// above it is as unwritable as a non-positive one, so it is refused in the same
// place rather than by a rule of its own — one predicate with three reasons
// cannot drift against itself.
//
// It can only ever reject an amount this resolution *derived*: a stored amount
// above the ceiling cannot exist, because the column it was read from could not
// have held it. So unlike the zero floor, this bound changes nothing about data
// already saved.
const MAX_WRITABLE_AMOUNT = 999999.99;

// A money value only reaches a patch when it is a real, positive number the
// column can hold. Zero and negatives are refused, so is anything the
// arithmetic could not carry, and so is anything past the ceiling above.
const isWritableAmount = (value: number | undefined): value is number =>
  value !== undefined &&
  Number.isFinite(value) &&
  value > 0 &&
  value <= MAX_WRITABLE_AMOUNT;

// The database columns are `decimal(8,2)`. Round here so the client and the
// server never disagree about the last two digits. The factor is not rounded —
// it is a ratio, and it is never transmitted.
const roundAmount = (value: number | undefined) =>
  value === undefined ? undefined : Math.round(value * 100) / 100;

const asNumber = (value: number | null | undefined) =>
  typeof value === 'number' ? value : undefined;

const times = (a: number | undefined, b: number | undefined) =>
  a === undefined || b === undefined ? undefined : a * b;

const dividedBy = (a: number | undefined, b: number | undefined) =>
  a === undefined || b === undefined || b === 0 ? undefined : a / b;

// Set-based resolution: `input` is the dataset's stored values with the entered
// ones already merged in, `changed` names the ones the user entered. The two
// amounts leave together or not at all (R26/R35).
const resolveChangedInventoryItemValues = (
  input: InventoryItemValues,
  changed: ReadonlySet<string>
): ResolvedInventoryItemValues => {
  const enteredLiquidation = changed.has('liquidation_value');
  const enteredContinuation = changed.has('continuation_value');
  const enteredFactor = changed.has('value_factor');

  // A caller that did not materialise the factor gets it resolved here, so a
  // bulk edit outside fe-web's grid does the same arithmetic.
  let value_factor: number | undefined =
    asNumber(input.value_factor) ??
    calculateValueFactorByAssetType(
      input.tangible_asset_type,
      input.liquidation_value,
      input.continuation_value
    );

  if (!enteredLiquidation && !enteredContinuation && !enteredFactor) {
    return {
      value_factor,
      liquidation_value: undefined,
      continuation_value: undefined,
    };
  }

  let liquidation_value: number | undefined;
  let continuation_value: number | undefined;

  if (enteredLiquidation && enteredContinuation) {
    // Over-determined or not, the two amounts win and the factor follows them.
    liquidation_value = asNumber(input.liquidation_value);
    continuation_value = asNumber(input.continuation_value);
    value_factor = dividedBy(continuation_value, liquidation_value);
  } else if (enteredLiquidation) {
    liquidation_value = asNumber(input.liquidation_value);
    continuation_value = times(liquidation_value, value_factor);
  } else if (enteredContinuation) {
    // R40/R43: the Fortführungswert never derives its partner. The dataset's
    // stored Liquidationswert is resent verbatim so the pair rule still holds,
    // and the factor follows the resulting pair — exactly as the branch above.
    // An entered Faktor beside it is ignored (R44/KTD25): the entered amount
    // outranks the entered ratio, and the ratio can no longer produce an amount.
    continuation_value = asNumber(input.continuation_value);
    liquidation_value = asNumber(input.liquidation_value);
    value_factor = dividedBy(continuation_value, liquidation_value);
  } else if (input.liquidation_value) {
    // Factor alone: the Liquidationswert is the only anchor there is (R41). A
    // dataset holding only a Fortführungswert resolves nothing and falls into
    // the no-money leaf below.
    liquidation_value = asNumber(input.liquidation_value);
    continuation_value = times(liquidation_value, value_factor);
  }

  const roundedLiquidation = roundAmount(liquidation_value);
  const roundedContinuation = roundAmount(continuation_value);
  const writable =
    isWritableAmount(roundedLiquidation) &&
    isWritableAmount(roundedContinuation);

  return {
    value_factor:
      value_factor !== undefined && Number.isFinite(value_factor)
        ? value_factor
        : undefined,
    liquidation_value: writable ? roundedLiquidation : undefined,
    continuation_value: writable ? roundedContinuation : undefined,
  };
};

export function calculateInventoryItemValues(
  input: InventoryItemValues,
  property: string
): ResolvedInventoryItemValues;
export function calculateInventoryItemValues(
  input: InventoryItemValues,
  changed: ReadonlySet<string>
): ResolvedInventoryItemValues;
export function calculateInventoryItemValues(
  input: InventoryItemValues,
  property: string | ReadonlySet<string>
): ResolvedInventoryItemValues {
  if (typeof property !== 'string') {
    return resolveChangedInventoryItemValues(input, property);
  }

  // No case writes `liquidation_value` any more (R40), so it is read-only here
  // again — the shape this function had before RIEG-261. `prefer-const` insists.
  const { tangible_asset_type, liquidation_value } = input;
  let { value_factor, continuation_value } = input;

  switch (property) {
    case 'tangible_asset_type':
      value_factor = calculateValueFactorByAssetType(
        tangible_asset_type,
        liquidation_value,
        continuation_value
      );

      if (liquidation_value && !continuation_value) {
        continuation_value = value_factor * liquidation_value;
      }
      break;

    case 'liquidation_value':
      continuation_value =
        (liquidation_value as number) * (value_factor as number);
      break;
    case 'continuation_value':
      // R40/R41: derivation is one-way, so this case only ever recomputes the
      // factor. With no liquidation value to divide by it changes nothing —
      // rather than returning `Infinity` (pre-RIEG-261) or inventing an amount
      // from the factor (during it). Same shape as `'value_factor'` below.
      if (liquidation_value) {
        value_factor = (continuation_value as number) / liquidation_value;
      }
      break;
    case 'value_factor':
      if (liquidation_value) {
        continuation_value =
          (value_factor as number) * (liquidation_value as number);
      }
      break;
  }

  return {
    value_factor,
    liquidation_value,
    continuation_value,
  };
}

// Entered values plus the selected datasets in, per-item patches out. Pure: the
// caller owns the request, the confirmation and every message.
export const buildInventoryBulkPatches = (
  entered: Partial<InventoryItem>,
  datasets: readonly InventoryBulkDataset[]
): InventoryBulkPatchPlan => {
  const enteredEntries = Object.entries(entered).filter(
    ([, value]) => value !== undefined
  );
  const enteredFields = new Set(enteredEntries.map(([field]) => field));
  const attemptsAmount = INTERLOCKED_VALUE_FIELDS.some((field) =>
    enteredFields.has(field)
  );
  // Everything the builder does not resolve travels verbatim. `value_factor` is
  // dropped here and nowhere else — it is not a stored column.
  const passThrough = enteredEntries.filter(
    ([field]) =>
      !(INTERLOCKED_VALUE_FIELDS as readonly string[]).includes(field)
  );

  const patches: InventoryBulkPatch[] = [];
  const seen = new Set<number>();

  for (const dataset of datasets) {
    if (seen.has(dataset.id)) continue;
    seen.add(dataset.id);

    const patch: InventoryBulkPatch = {
      id: dataset.id,
      ...Object.fromEntries(passThrough),
    };

    if (attemptsAmount) {
      const { liquidation_value, continuation_value } =
        calculateInventoryItemValues(
          {
            tangible_asset_type: enteredFields.has('tangible_asset_type')
              ? entered.tangible_asset_type
              : dataset.tangible_asset_type,
            liquidation_value: enteredFields.has('liquidation_value')
              ? entered.liquidation_value
              : dataset.liquidation_value,
            continuation_value: enteredFields.has('continuation_value')
              ? entered.continuation_value
              : dataset.continuation_value,
            value_factor: enteredFields.has('value_factor')
              ? entered.value_factor
              : // The dataset's own factor: its stored ratio where both amounts
                // exist, its asset type's preset otherwise (R34).
                dataset.value_factor ??
                calculateValueFactorByAssetType(
                  dataset.tangible_asset_type,
                  dataset.liquidation_value,
                  dataset.continuation_value
                ),
          },
          enteredFields
        );

      // KTD27: both amounts or neither (R43's pair rule). A dataset that
      // resolved nothing is not recorded anywhere — the patch it produces
      // simply carries no money field.
      if (
        typeof liquidation_value === 'number' &&
        typeof continuation_value === 'number'
      ) {
        patch.liquidation_value = liquidation_value;
        patch.continuation_value = continuation_value;
      }
    }

    if (Object.keys(patch).length > 1) {
      patches.push(patch);
    }
  }

  return { patches };
};

// The pure half of reconciliation: everything submitted that the response
// accounted for neither as updated nor as failed. The caller words it.
export const reconcileInventoryBulkUpdate = (
  submittedIds: readonly number[],
  updated: readonly number[] | null | undefined,
  failed: readonly { id: number }[] | null | undefined
): number[] => {
  const accounted = new Set<number>([
    ...(updated ?? []),
    ...(failed ?? []).map((entry) => entry.id),
  ]);

  return submittedIds.filter((id) => !accounted.has(id));
};

/**
 * The title and brand values a project already uses, one spelling per group
 * that differs only in letter case or whitespace, as the server would store
 * it. The services hand both lists over in German order.
 */
export type InventoryItemSuggestions = {
  title: string[];
  brand: string[];
};

export const NO_INVENTORY_ITEM_SUGGESTIONS: InventoryItemSuggestions = {
  title: [],
  brand: [],
};

const GERMAN = new Intl.Collator('de');

/**
 * Both lists in German order (umlauts next to their base letters), which the
 * server cannot do.
 */
export function sortedInventoryItemSuggestions(
  suggestions: InventoryItemSuggestions
): InventoryItemSuggestions {
  return {
    title: [...suggestions.title].sort(GERMAN.compare),
    brand: [...suggestions.brand].sort(GERMAN.compare),
  };
}

/**
 * The suggestion `value` stands for: the same text apart from letter case and
 * whitespace, which is how the server folds the lists. Undefined when there is
 * none.
 */
export function matchingSuggestion(
  value: string,
  suggestions: readonly string[]
): string | undefined {
  const key = suggestionKey(value);
  return suggestions.find((suggestion) => suggestionKey(suggestion) === key);
}

function suggestionKey(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}
